import {
  SESSION_AUTH_HEADER,
  SESSION_AUTH_REQUIRED,
  errorEnvelopeSchema,
  type ErrorCode,
} from '@castinflow/shared';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly requestId: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const CSRF_HEADER = 'x-csrf-token';

/**
 * v3.1 B3: çalışma alanı askıya alındı (423 `TENANT_SUSPENDED`). Kabuk dinler ve askı ekranına geçer; giriş ekranı kendi
 * iletisini gösterir (dinleyici yalnız oturum açık kabukta kayıtlıdır).
 */
type SuspendedListener = (message: string) => void;
const suspendedListeners = new Set<SuspendedListener>();
export function onTenantSuspended(l: SuspendedListener): () => void {
  suspendedListeners.add(l);
  return () => {
    suspendedListeners.delete(l);
  };
}
/** Çift gönderim çerezi: üretimde `__Host-` önekli olabilir (API `COOKIE_PREFIX`); ikisi de okunur, önekli öncelikli. */
const CSRF_COOKIES = ['__Host-cf_csrf', 'cf_csrf'];

function readCookie(name: string): string | null {
  // Tarayıcı dışı bağlamda (birim testi, ön işleme) çerez yoktur
  if (typeof document === 'undefined') return null;
  const m = new RegExp(`(?:^|; )${name}=([^;]*)`).exec(document.cookie);
  return m?.[1] ?? null;
}

/** Oturumun CSRF belirteci (yoksa `null`). Belirteç her oturum yenilemesinde değişir. */
export function csrfToken(): string | null {
  for (const name of CSRF_COOKIES) {
    const v = readCookie(name);
    if (v !== null && v.length > 0) return v;
  }
  return null;
}

/**
 * 401 sonrası sessiz yenileme DENENMEYEN yollar: kimlik bilgisi/tek kullanımlık belirteç sunan uçlarda 401 "bilgiler geçersiz"
 * demektir; yenileme ucunun kendisi ve oturumsuz herkese açık uçlar da dışarıdadır.
 */
const NO_REFRESH = /^\/(auth\/(login|refresh|password|invite\/accept)|signup|public)(\/|\?|$)/;
const REFRESH_LOCK = 'castinflow-session-refresh';

let refreshing: Promise<boolean> | null = null;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Oturum yenileme (M6 WP 6.3): access belirteci kısa ömürlüdür; süresi dolunca refresh çereziyle yeni oturum alınır.
 * `seen` = 401 alan isteğin gönderildiği andaki CSRF belirteci. Belirteç o andan beri değiştiyse başka bir istek/sekme
 * yenilemeyi zaten yapmıştır; ikinci kez yenilenmez (aynı refresh belirtecinin iki kez sunulması sunucuda yeniden kullanım
 * sayılır). Sunucu 409 dönerse (eşzamanlı yenileme) kazananın çerezlerinin yazılması kısa süre beklenir.
 */
async function refreshOnce(seen: string | null): Promise<boolean> {
  const current = csrfToken();
  if (current === null) return false; // bu tarayıcıda oturum hiç açılmamış / çıkış yapılmış
  if (seen !== null && current !== seen) return true;
  let res: Response;
  try {
    res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'application/json', [CSRF_HEADER]: current },
    });
  } catch {
    return false; // ağ hatası: asıl 401 çağırana döner
  }
  if (res.ok) return true;
  if (res.status !== 409) return false;
  for (let i = 0; i < 30; i += 1) {
    if (csrfToken() !== current) return true;
    await sleep(100);
  }
  return false;
}

/** Sekme içinde tek uçuş; sekmeler arasında Web Locks ile sıralı (destek yoksa sunucunun 409 yolu devreye girer). */
function refreshSession(seen: string | null): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      // Web Locks her tarayıcıda/bağlamda yoktur (eski sürümler, güvenli olmayan köken); tip tanımı var sayar
      const locks = (navigator as unknown as { locks?: LockManager }).locks;
      if (locks === undefined) return await refreshOnce(seen);
      return await locks.request(REFRESH_LOCK, () => refreshOnce(seen));
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function send(path: string, init: RequestInit, headers: Headers): Promise<Response> {
  return fetch(`/api${path}`, { credentials: 'same-origin', ...init, headers });
}

/**
 * Aynı origin /api önekiyle API çağrısı; hata zarfını ApiError'a çevirir.
 * Oturum katmanının 401'inde (yanıt `x-session-auth: required` taşır) oturum bir kez sessizce yenilenir ve istek YENİDEN
 * gönderilir: o 401 kimlik doğrulama ara katmanında, işlem başlamadan verildiği için yeniden gönderim (mutasyonlar dahil)
 * ikinci bir etki üretmez. CSRF başlığı yeni belirteçle güncellenir. İşaretsiz 401 çağırana olduğu gibi döner.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('accept')) headers.set('accept', 'application/json');
  const seen = csrfToken();
  let res = await send(path, init, headers);
  // Yalnız OTURUM katmanının 401'i yenilenir (işaretli). Yeniden kimlik doğrulamada yanlış parola/kod da 401 döner ama o bir
  // iş yanıtıdır: yenileyip yeniden göndermek aynı yanlış bilgiyi ikinci kez sunar (iki deneme sayılır).
  if (
    res.status === 401 &&
    res.headers.get(SESSION_AUTH_HEADER) === SESSION_AUTH_REQUIRED &&
    !NO_REFRESH.test(path) &&
    (await refreshSession(seen))
  ) {
    const fresh = csrfToken();
    if (headers.has(CSRF_HEADER) && fresh !== null) headers.set(CSRF_HEADER, fresh);
    res = await send(path, init, headers);
  }
  const text = await res.text();
  const json: unknown = text.length > 0 ? JSON.parse(text) : null;
  if (!res.ok) {
    const parsed = errorEnvelopeSchema.safeParse(json);
    if (parsed.success) {
      const e = parsed.data.error;
      if (res.status === 423 && e.code === 'TENANT_SUSPENDED')
        for (const l of suspendedListeners) l(e.message);
      throw new ApiError(e.code, e.message, e.requestId, res.status, e.details);
    }
    throw new ApiError(
      'INTERNAL',
      `HTTP ${res.status}`,
      res.headers.get('x-request-id') ?? '-',
      res.status,
    );
  }
  return json as T;
}
